import axios from 'axios';
import router from '@/router/index';
import { useAuthStore } from '@/modules/auth/store';
import { showToast } from '@/utilities/toast-handler';

const authApi = axios.create({
    baseURL: process.env.VUE_APP_API_URL,
});

authApi.defaults.headers.common['Content-Type'] = 'application/json';

authApi.interceptors.request.use(
    (config) => {
        const authStore = useAuthStore();
        config.headers.Authorization = `Bearer ${authStore.token}`;
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

let retries = 0;
let unauthenticatedFlag = false;

// add response interceptors to handle response data and errors
authApi.interceptors.response.use(
    (response) => {
        const status = response.status;
        // check blob type
        const blobType = response.data instanceof Blob;
        if (status === 200 || status === 201) {
            return { statusCode: status, toast: { severity: 'success', summary: 'Success' }, data: blobType ? response.data : response.data.data };
        }
        return { statusCode: response.status, data: response.data.data };
    },
    (error) => {
        // console.log(error);
        // Check if the unauthenticatedFlag is set
        if (unauthenticatedFlag) {
            // If the flag is set, don't proceed with the request
            return Promise.reject(error);
        }
        // Check if its a network error
        if (error.code === 'ERR_NETWORK') {
            // Just show network error for now, this is to avoid longer requests getting retried over and over again as well
            showToast({}, 'Network Error');
        } else if (retries < 2) {
            // Retry the request up to 2 times
            retries++;
            return authApi(error.config);
        } else {
            // server error
            if (error?.response?.status === 500) {
                showToast({}, 'Server Error');
            }
            // check if user is trying to navigate to a url which the user doesnt have access to
            // (error.code === 'ERR_BAD_REQUEST')
            else if (error.response.status === 403) {
                // for forbidden requests redirect to Access page
                // router.push({ name: 'Access' });
                showToast({}, 'Not enough permissions to access');
            }
            // Check if there is an issue with the url, if yes then push to NotFound page
            // else if (error.response.status === 404) {
            //     return router.push({ name: 'NotFound', params: { resource: error?.response?.data?.message } });
            // }
            // Check if authentication error or token expiration has occurred
            else if (error.response) {
                if (error.response.data?.message?.includes('Unauthenticated')) {
                    const authStore = useAuthStore();
                    // Show the re-login dialog
                    showToast({}, 'User Session has expired, Login to continue..');
                    setTimeout(async () => {
                        // trigger logout function here
                        // log out the user from the state
                        // clean up authstore cache to avoid loops
                        await authStore.logout();
                        // then redirect to login
                        router.push({ name: 'InvalidToken' });
                    }, 3000);
                }
            }
            // And if its related to some other error issues then
            return Promise.reject(error.response);
        }
    }
);

// Function to reset unauthenticatedFlag when a user logs in
export function resetUnauthenticatedFlag() {
    unauthenticatedFlag = false;
}

// Create a new Axios instance with custom configuration
const templateApi = axios.create({
    baseURL: process.env.VUE_APP_TEMPLATE_API,
});

templateApi.defaults.headers.common['Content-Type'] = 'application/json';

// Add interceptors to handle request and response
templateApi.interceptors.request.use(
    (config) => {
        // Add your custom authorization logic here
        config.headers.Authorization = 'Token ' + process.env.VUE_APP_TEMPLATE_API_KEY;
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

export { templateApi, authApi };
